import { ChevronDown, Grid2X2, LayoutGrid, Square } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { useCaseMetadata } from '@/hooks/use-case-metadata';
import type { LayoutPreset } from '@/visualization/services/viewport-layout-service';

export interface ViewerHeaderProps {
    /** Currently selected layout */
    currentLayout: LayoutPreset | null;
    /** Available layouts to choose from */
    availableLayouts: LayoutPreset[];
    /** Callback when layout is changed */
    onLayoutChange: (layoutName: string) => void;
    /** Callback when back button is clicked */
    onBack: () => void;
    /** Optional extra content to render in the right section (e.g., save status) */
    extra?: React.ReactNode;
    /** When true the active step renders a component instead of VTK viewports — snapshot capture is disabled. */
    isComponentStep?: boolean;
}

/**
 * Format patient name from firstname and lastname.
 */
function formatPatientName(firstname: string | null, lastname: string | null): string {
    const parts = [firstname, lastname].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : 'Unknown Patient';
}

/**
 * Get icon for a layout preset based on its configuration.
 */
function getLayoutIcon(layout: LayoutPreset) {
    const viewportCount = layout.viewports.length;
    if (viewportCount === 1) {
        return <Square className="h-4 w-4" />;
    }
    if (viewportCount === 2) {
        return <LayoutGrid className="h-4 w-4" />;
    }
    return <Grid2X2 className="h-4 w-4" />;
}

/**
 * ViewerHeader - Header component for the viewer page.
 *
 * Contains:
 * - Back navigation with logo
 * - Toolbar with layout selector
 * - Case/patient information display (sourced from workflow service metadata)
 */
export function ViewerHeader({
    currentLayout,
    availableLayouts,
    onLayoutChange,
    onBack,
    extra,
    isComponentStep = false,
}: ViewerHeaderProps) {
    const metadata = useCaseMetadata();
    const caseName = metadata?.name ?? '';
    const patientName = metadata ? formatPatientName(metadata.firstname, metadata.lastname) : '';

    return (
        <header className="flex h-14 items-center justify-between border-b border-border bg-background px-4">
            {/* Left section: Logo/back button */}
            <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={onBack} className="gap-2 px-2">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="h-5 w-5"
                    >
                        <path d="M15 18l-6-6 6-6" />
                    </svg>
                    <span className="font-semibold">ESCloud</span>
                </Button>
            </div>

            {/* Center section: Toolbar */}
            <div className="flex items-center gap-2">
                {/* Layout selector */}
                {!isComponentStep && (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm" className="gap-2">
                                {currentLayout ? (
                                    getLayoutIcon(currentLayout)
                                ) : (
                                    <Square className="h-4 w-4" />
                                )}
                                <span className="hidden sm:inline">{currentLayout?.label ?? 'Layout'}</span>
                                <ChevronDown className="h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="center">
                            {availableLayouts.map((layout) => (
                                <DropdownMenuItem
                                    key={layout.name}
                                    onClick={() => onLayoutChange(layout.name)}
                                    className="gap-2"
                                >
                                    {getLayoutIcon(layout)}
                                    {layout.label}
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>
                )}
            </div>

            {/* Right section: Case info */}
            <div className="flex items-center gap-3">
                {extra}
                {extra && <Separator orientation="vertical" className="h-8" />}
                <div className="text-right">
                    <p className="text-sm font-medium leading-none">{caseName}</p>
                    <p className="text-xs text-muted-foreground">{patientName}</p>
                </div>
                <Separator orientation="vertical" className="h-8" />
                <div className="h-8 w-8 rounded-full bg-muted" />
            </div>
        </header>
    );
}
